import classNames from 'classnames';
import type { ReactElement, Ref, RefObject } from 'react';
import { useEffect, useRef } from 'react';
import styles from './InputField.module.scss';
import type { InputFieldProps, InputFieldRef } from './InputField.data';
import Text from '../text/Text';
import Icon from '../icon/Icon';
import gsap from 'gsap';

const resizeTextArea = (textAreaRef: RefObject<HTMLTextAreaElement | null>) => {
  if (textAreaRef.current) {
    textAreaRef.current.style.height = '0px';
    const { scrollHeight } = textAreaRef.current;
    gsap.set(textAreaRef.current, {
      height: scrollHeight,
    });
  }
};

export default function InputField({
  type,
  maxlength,
  label,
  name,
  register,
  error,
  placeholder,
  hidden,
  required,
  Autocomplete,
  options,
  ref,
  ...props
}: InputFieldProps & { ref?: Ref<InputFieldRef> }): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    resizeTextArea(textAreaRef);
  }, [textAreaRef]);

  let params;
  if (typeof window !== 'undefined') {
    params = new URLSearchParams(window.location.search);
  }

  const Input = type === 'textarea' ? 'textarea' : 'input';

  return (
    <div
      ref={elementRef}
      className={classNames(styles.inputField, props.className, {
        [styles.isInvalid]: !!error,
        [styles.hidden]: hidden,
      })}
    >
      <div className={classNames(styles.inputContainer)}>
        <Text
          disableTriggerInOnScroll={false}
          as="label"
          htmlFor={name}
          size="label-small"
          className={classNames(styles.label)}
          transitionInType="splitWordsSlideInUp"
        >
          {label} {required ? '*' : ''}
        </Text>

        {Autocomplete ? (
          <Autocomplete
            register={register}
            name={name}
            menuClassName={styles.autocompleteMenu}
            options={options}
            index={props.index}
            defaultProps={{
              inputProps: {
                className: classNames(styles.input, styles['input-field'], 'input-field'),
                id: name,
                placeholder,
                maxLength: maxlength,
                spellCheck: 'false',
                autoComplete: 'off',
                ...register(name),
              },
            }}
          />
        ) : (
          <Input
            className={classNames(styles.input, 'input-field')}
            {...register(name)}
            // @ts-ignore
            ref={(ref: HTMLInputElement | HTMLTextAreaElement) => {
              if (type === 'textarea') {
                // @ts-ignore
                textAreaRef.current = ref;
              }
              register(name).ref(ref);
            }}
            id={name}
            type={type}
            name={name}
            maxLength={maxlength}
            placeholder={placeholder}
            spellCheck="false"
            autoComplete="off"
            defaultValue={params?.get(name) || undefined}
            required={required}
            onChange={() => {
              resizeTextArea(textAreaRef);
            }}
          />
        )}
      </div>

      <div className={classNames(styles.errorContainer)}>
        {error?.message && (
          <>
            <Icon className={classNames(styles.icon)} name="error" />
            <Text
              as="span"
              size="foot-note"
              className={classNames(styles.errorMessage)}
              transitionInType="none"
            >
              <>{error?.message}</>
            </Text>
          </>
        )}
      </div>
    </div>
  );
}
